import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pastilleStatut, PRIX_CATALOGUE, PRIX_OEUVRE_CATALOGUE } from '@artosera/shared';
import {
  Badge,
  Button,
  ButtonLink,
  Checkbox,
  Dialog,
  Input,
  Price,
  Select,
  Switch,
  Textarea,
} from '@artosera/ui';
import {
  catalogueVisiteApi,
  useCatalogueVisite,
  useEnregistrerCatalogueVisite,
  useRegenererCatalogueVisite,
  useStatistiquesCatalogueVisite,
  type CatalogueVisitePresente,
  type FormatEtiquettesVisite,
  type OeuvreCatalogueVisite,
} from '../../../api/catalogueVisite.js';
import { enregistrerFichier } from '../../../api/client.js';
import { dansLaPorteeCourante } from '../../../api/cles.js';
import { estApiError } from '../../../api/errors.js';
import { estPorteeObsolete } from '../../../auth/identite.js';
import { Field } from '../../Field.js';
import { VignetteSalle } from '../../viewing-rooms/salleUi.js';
import commun from './Tabs.module.css';
import styles from './CatalogueVisiteTab.module.css';

export function CatalogueVisiteTab({
  galerieSlug,
  expositionId,
  peutEcrire,
}: {
  galerieSlug: string;
  expositionId: string;
  peutEcrire: boolean;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun', 'oeuvres']);
  const langue = i18n.language.startsWith('en') ? 'en' : 'fr';
  const fiche = useCatalogueVisite(galerieSlug, expositionId);
  const statistiques = useStatistiquesCatalogueVisite(galerieSlug, expositionId);
  const sauvegarde = useEnregistrerCatalogueVisite(galerieSlug, expositionId);
  const regeneration = useRegenererCatalogueVisite(galerieSlug, expositionId);
  const [brouillon, setBrouillon] = useState<CatalogueVisitePresente | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [retour, setRetour] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState(false);
  const [languePdf, setLanguePdf] = useState<'fr' | 'en'>(langue);
  const [pdfEnCours, setPdfEnCours] = useState(false);
  const catalogue = brouillon ?? fiche.data;
  const enCours = sauvegarde.isPending || regeneration.isPending;
  const prefixe = 'regie:expositions.catalogueVisite';

  function modifier(champs: Partial<CatalogueVisitePresente>) {
    if (!peutEcrire || !catalogue) return;
    setBrouillon({ ...catalogue, ...champs });
    setRetour(null);
  }

  function modifierOeuvre(id: string, champs: Partial<OeuvreCatalogueVisite>) {
    if (!catalogue) return;
    modifier({
      oeuvres: catalogue.oeuvres.map((oeuvre) =>
        oeuvre.oeuvreId === id ? { ...oeuvre, ...champs } : oeuvre,
      ),
    });
  }

  async function enregistrer() {
    if (!peutEcrire || !catalogue || enCours) return;
    setErreur(null);
    setRetour(null);
    try {
      await sauvegarde.mutateAsync({
        publie: catalogue.publie,
        visibleJusquAu: catalogue.visibleJusquAu,
        prix: catalogue.prix,
        texte: catalogue.texte,
        accuseReception: catalogue.accuseReception,
        revision: catalogue.revision,
        oeuvres: catalogue.oeuvres.map(({ oeuvreId, masquee, prix, texteSalle }) => ({
          oeuvreId,
          masquee,
          prix,
          texteSalle,
        })),
      });
      setBrouillon(null);
      setRetour(t(`${prefixe}.enregistre`));
    } catch (echec) {
      if (estPorteeObsolete(echec)) return;
      if (estApiError(echec) && echec.status === 409) {
        setErreur(t(`${prefixe}.conflit`));
        setBrouillon(null);
        await fiche.refetch();
      } else {
        setErreur(estApiError(echec) ? echec.message : t('commun:erreurReseau'));
      }
    }
  }

  async function regenerer() {
    if (!peutEcrire || !fiche.data?.existe || enCours || brouillon) return;
    setErreur(null);
    setRetour(null);
    try {
      await regeneration.mutateAsync();
      setConfirmation(false);
      setRetour(t(`${prefixe}.regenere`));
    } catch (echec) {
      if (!estPorteeObsolete(echec))
        setErreur(estApiError(echec) ? echec.message : t('commun:erreurReseau'));
    }
  }

  async function telecharger(format: FormatEtiquettesVisite | 'affiche') {
    if (!fiche.data?.existe || pdfEnCours) return;
    setErreur(null);
    setPdfEnCours(true);
    try {
      const fichier = await dansLaPorteeCourante((appel) =>
        format === 'affiche'
          ? catalogueVisiteApi.affichePdf(galerieSlug, expositionId, languePdf, appel)
          : catalogueVisiteApi.etiquettesPdf(galerieSlug, expositionId, format, languePdf, appel),
      );
      enregistrerFichier(fichier, `catalogue-visite-${format}-${languePdf}.pdf`);
    } catch (echec) {
      if (estPorteeObsolete(echec)) return;
      if (estApiError(echec) && echec.status === 409) {
        setErreur(t(`${prefixe}.enregistrerDabord`));
        await fiche.refetch();
      } else {
        setErreur(estApiError(echec) ? echec.message : t('commun:erreurReseau'));
      }
    } finally {
      setPdfEnCours(false);
    }
  }

  async function copier(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setRetour(t(`${prefixe}.copie`));
    } catch {
      setErreur(t(`${prefixe}.erreurCopie`));
    }
  }

  if (fiche.isPending) return <p>{t('commun:chargement')}</p>;
  if (fiche.isError || !catalogue)
    return (
      <p role="alert">
        {estApiError(fiche.error) ? fiche.error.message : t('commun:erreurReseau')}
      </p>
    );

  return (
    <div className={commun.formulaire}>
      <div className={styles.actions}>
        <h2>{t('regie:expositions.onglets.catalogueVisite')}</h2>
        <Badge tone={fiche.data?.etat === 'publie' ? 'available' : 'neutral'}>
          {t(`${prefixe}.etats.${fiche.data!.etat}`)}
        </Badge>
      </div>
      <fieldset className={styles.champs} disabled={!peutEcrire || enCours}>
        <Switch
          id="visite-publie"
          label={t(`${prefixe}.publie`)}
          checked={catalogue.publie}
          onCheckedChange={(publie) => modifier({ publie })}
        />
        <div className={commun.grille2}>
          <Field label={t(`${prefixe}.visibleJusquAu`)} htmlFor="visite-fin">
            <Input
              id="visite-fin"
              type="date"
              value={catalogue.visibleJusquAu?.slice(0, 10) ?? ''}
              onChange={(event) =>
                modifier({
                  visibleJusquAu: event.target.value ? `${event.target.value}T23:59:59.999Z` : null,
                })
              }
            />
            <p className={commun.aide}>{t(`${prefixe}.echeanceDefaut`)}</p>
          </Field>
          <Field label={t(`${prefixe}.prixCatalogue`)} htmlFor="visite-prix">
            <Select
              id="visite-prix"
              aria-label={t(`${prefixe}.prixCatalogue`)}
              value={catalogue.prix}
              onValueChange={(prix) => modifier({ prix: prix as CatalogueVisitePresente['prix'] })}
              options={PRIX_CATALOGUE.map((value) => ({
                value,
                label: t(`${prefixe}.prix.${value}`),
              }))}
            />
          </Field>
        </div>
        <Checkbox
          id="visite-accuse"
          label={t(`${prefixe}.accuseReception`)}
          checked={catalogue.accuseReception}
          onCheckedChange={(valeur) => modifier({ accuseReception: valeur === true })}
        />
        <p className={commun.aide}>{t(`${prefixe}.texteDefaut`)}</p>
        <div className={commun.grille2}>
          {(['fr', 'en'] as const).map((code) => (
            <Field
              key={code}
              label={t(`${prefixe}.texte.${code}`)}
              htmlFor={`visite-texte-${code}`}
            >
              <Textarea
                id={`visite-texte-${code}`}
                rows={4}
                maxLength={4000}
                value={catalogue.texte[code] ?? ''}
                placeholder={catalogue.texteEffectif[code]}
                onChange={(event) =>
                  modifier({ texte: { ...catalogue.texte, [code]: event.target.value } })
                }
              />
            </Field>
          ))}
        </div>
      </fieldset>

      <h3>{t(`${prefixe}.oeuvres`)}</h3>
      <p className={commun.aide}>{t(`${prefixe}.prixEffectifAide`)}</p>
      <div className={styles.defilement}>
        <table className={styles.tableau}>
          <thead>
            <tr>
              {['oeuvre', 'masquer', 'prixOeuvre', 'texteSalle', 'liens'].map((cle) => (
                <th key={cle} scope="col">
                  {t(`${prefixe}.${cle}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalogue.oeuvres.map((oeuvre) => {
              const pastille = pastilleStatut(oeuvre.statut);
              return (
                <tr key={oeuvre.oeuvreId}>
                  <th scope="row">
                    <div className={styles.oeuvre}>
                      <VignetteSalle galerieSlug={galerieSlug} mediaId={oeuvre.mediaPrincipalId} />
                      <div>
                        <strong>{oeuvre.titre}</strong>
                        <p>{oeuvre.artisteNom}</p>
                        {pastille && (
                          <Badge tone={pastille === 'vendue' ? 'sold' : 'reserved'}>
                            {t(`oeuvres:statuts.${pastille}`)}
                          </Badge>
                        )}
                        {!oeuvre.servie && (
                          <p className={commun.aide}>{t(`${prefixe}.nonServie`)}</p>
                        )}
                      </div>
                    </div>
                  </th>
                  <td>
                    <Checkbox
                      id={`visite-masquer-${oeuvre.oeuvreId}`}
                      label={t(`${prefixe}.masquerOeuvre`, { titre: oeuvre.titre })}
                      disabled={!peutEcrire || enCours}
                      checked={oeuvre.masquee}
                      onCheckedChange={(valeur) =>
                        modifierOeuvre(oeuvre.oeuvreId, { masquee: valeur === true })
                      }
                    />
                  </td>
                  <td>
                    <Select
                      aria-label={t(`${prefixe}.prixDeOeuvre`, { titre: oeuvre.titre })}
                      value={oeuvre.prix}
                      disabled={!peutEcrire || enCours}
                      onValueChange={(prix) =>
                        modifierOeuvre(oeuvre.oeuvreId, {
                          prix: prix as OeuvreCatalogueVisite['prix'],
                        })
                      }
                      options={PRIX_OEUVRE_CATALOGUE.map((value) => ({
                        value,
                        label: t(`${prefixe}.prix.${value}`),
                      }))}
                    />
                    <p>
                      {oeuvre.prixAffiche && oeuvre.prixPublicCentimes !== null ? (
                        <Price
                          cents={oeuvre.prixPublicCentimes}
                          currency={oeuvre.devise}
                          lang={langue}
                        />
                      ) : (
                        t(`${prefixe}.prixNonAffiche`)
                      )}
                    </p>
                  </td>
                  <td>
                    {(['fr', 'en'] as const).map((code) => (
                      <Field
                        key={code}
                        label={t(`${prefixe}.texteSalleLangue.${code}`)}
                        htmlFor={`visite-salle-${oeuvre.oeuvreId}-${code}`}
                      >
                        <Textarea
                          id={`visite-salle-${oeuvre.oeuvreId}-${code}`}
                          rows={3}
                          maxLength={2000}
                          disabled={!peutEcrire || enCours}
                          value={oeuvre.texteSalle[code] ?? ''}
                          onChange={(event) =>
                            modifierOeuvre(oeuvre.oeuvreId, {
                              texteSalle: { ...oeuvre.texteSalle, [code]: event.target.value },
                            })
                          }
                        />
                      </Field>
                    ))}
                  </td>
                  <td>
                    {fiche.data?.existe && oeuvre.servie && (
                      <div className={styles.actions}>
                        <Button variant="ghost" size="sm" onClick={() => void copier(oeuvre.url)}>
                          {t(`${prefixe}.copier`)}
                        </Button>
                        <ButtonLink
                          variant="secondary"
                          size="sm"
                          href={`${oeuvre.url}?lang=${langue}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink size={16} strokeWidth={1.5} aria-hidden />
                          {t(`${prefixe}.ouvrir`)}
                        </ButtonLink>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {catalogue.oeuvres.length === 0 && (
        <p className={commun.aide}>{t(`${prefixe}.aucuneOeuvre`)}</p>
      )}
      {peutEcrire && (
        <Button
          onClick={() => void enregistrer()}
          loading={sauvegarde.isPending}
          disabled={regeneration.isPending}
        >
          {t(`${prefixe}.enregistrer`)}
        </Button>
      )}

      <section className={styles.champs}>
        <h3>{t(`${prefixe}.liensPdf`)}</h3>
        {!fiche.data?.existe && <p>{t(`${prefixe}.enregistrerDabord`)}</p>}
        {fiche.data?.existe && (
          <>
            <Field label={t(`${prefixe}.lienAccueil`)} htmlFor="visite-url">
              <Input
                id="visite-url"
                readOnly
                value={fiche.data.url}
                onFocus={(event) => event.currentTarget.select()}
              />
            </Field>
            <div className={styles.actions}>
              <Button variant="secondary" onClick={() => void copier(fiche.data!.url)}>
                {t(`${prefixe}.copier`)}
              </Button>
              <ButtonLink
                variant="secondary"
                href={`${fiche.data.url}?lang=${langue}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={16} strokeWidth={1.5} aria-hidden />
                {t(`${prefixe}.ouvrir`)}
              </ButtonLink>
            </div>
            {fiche.data.etat !== 'publie' && (
              <p className={commun.aide}>{t(`${prefixe}.publicFerme`)}</p>
            )}
          </>
        )}
        {brouillon && <p className={commun.aide}>{t(`${prefixe}.modificationsEnAttente`)}</p>}
        <Field label={t(`${prefixe}.languePdf`)} htmlFor="visite-langue-pdf">
          <Select
            id="visite-langue-pdf"
            aria-label={t(`${prefixe}.languePdf`)}
            value={languePdf}
            onValueChange={(valeur) => setLanguePdf(valeur as 'fr' | 'en')}
            options={(['fr', 'en'] as const).map((value) => ({
              value,
              label: t(`${prefixe}.langues.${value}`),
            }))}
          />
        </Field>
        <div className={styles.actions}>
          {(['cartel', 'a6', 'a4-8', 'affiche'] as const).map((format) => (
            <Button
              key={format}
              variant="secondary"
              disabled={!fiche.data?.existe || pdfEnCours || enCours}
              onClick={() => void telecharger(format)}
            >
              {t(`${prefixe}.pdf.${format}`)}
            </Button>
          ))}
          {peutEcrire && (
            <Button
              variant="danger"
              disabled={!fiche.data?.existe || enCours || pdfEnCours || brouillon !== null}
              onClick={() => setConfirmation(true)}
            >
              {t(`${prefixe}.regenerer`)}
            </Button>
          )}
        </div>
      </section>
      <section>
        <h3>{t(`${prefixe}.statistiques`)}</h3>
        {statistiques.isPending && <p>{t('commun:chargement')}</p>}
        {statistiques.isError && (
          <p role="alert">
            {estApiError(statistiques.error)
              ? statistiques.error.message
              : t('commun:erreurReseau')}
          </p>
        )}
        {statistiques.data && (
          <>
            <p>
              {t(`${prefixe}.ouvertures30Jours`, {
                count: statistiques.data.ouverturesParJour.reduce(
                  (total, jour) => total + jour.total,
                  0,
                ),
              })}
            </p>
            <h4>{t(`${prefixe}.topOeuvres`)}</h4>
            <ol>
              {statistiques.data.topOeuvres.map((ligne) => (
                <li key={ligne.oeuvreId}>
                  {catalogue.oeuvres.find((oeuvre) => oeuvre.oeuvreId === ligne.oeuvreId)?.titre ??
                    t(`${prefixe}.oeuvreDecrochee`)}{' '}
                  — {ligne.ouvertures}
                </li>
              ))}
            </ol>
            <h4>{t(`${prefixe}.demandesParOeuvre`)}</h4>
            <ul>
              {statistiques.data.oeuvres.map((ligne) => (
                <li key={ligne.oeuvreId}>
                  {catalogue.oeuvres.find((oeuvre) => oeuvre.oeuvreId === ligne.oeuvreId)?.titre ??
                    t(`${prefixe}.oeuvreDecrochee`)}{' '}
                  — {ligne.demandes}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
      {erreur && (
        <p className={commun.erreur} role="alert">
          {erreur}
        </p>
      )}
      {retour && (
        <p className={commun.succes} role="status">
          {retour}
        </p>
      )}
      {peutEcrire && (
        <Dialog
          open={confirmation}
          onOpenChange={(ouvert) => {
            if (!regeneration.isPending) setConfirmation(ouvert);
          }}
          title={t(`${prefixe}.confirmationTitre`)}
          description={t(`${prefixe}.confirmationTexte`)}
          closeLabel={t('commun:actions.fermer')}
          footer={
            <>
              <Button
                variant="ghost"
                disabled={regeneration.isPending}
                onClick={() => setConfirmation(false)}
              >
                {t('commun:actions.annuler')}
              </Button>
              <Button
                variant="danger"
                loading={regeneration.isPending}
                onClick={() => void regenerer()}
              >
                {t(`${prefixe}.confirmerRegeneration`)}
              </Button>
            </>
          }
        >
          {erreur && <p role="alert">{erreur}</p>}
        </Dialog>
      )}
    </div>
  );
}
